"use client";

import { useEffect, useId, useRef, useState } from "react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { BookOpen, LayoutDashboard, LifeBuoy, LogOut, User } from "lucide-react";
import type { AuthUser } from "@/features/auth/api";
import { useAuth } from "@/components/providers/auth-provider";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { cn } from "@/lib/utils";
import { toPublicProfileSlug, userInitials } from "@/features/playground/lib/public-profile-slug";
import { supportHomeHref } from "@/features/support/links";
import { IntentLink } from "./IntentLink";

/** The account photo, or the initials on the brand orange; the same face the Judge bar shows. */
export function AccountAvatar({ user, className }: { user: AuthUser; className?: string }) {
  const avatarUrl = user.avatarImageId ? cfImageUrl(user.avatarImageId, "avatar") : "";
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-bold text-white",
        className,
      )}
      aria-hidden
    >
      {avatarUrl ? (
        <Image src={avatarUrl} alt="" width={36} height={36} className="h-full w-full object-cover" />
      ) : (
        userInitials(user)
      )}
    </span>
  );
}

const itemClass =
  "flex min-h-10 w-full items-center gap-2.5 rounded-lg px-3 text-sm text-neutral-700 transition-colors duration-150 ease-out hover:bg-neutral-50 hover:text-neutral-900 focus-visible:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary";

/**
 * `SITE-NAV01`: the signed-in account control on the site bar, in both
 * languages. The old one wrote "Dashboard", "My Library" and "Sign out" as
 * English literals, so a Bangla page showed them in English (review N3).
 *
 * A disclosure rather than a Radix menu: the site bar is on every public page
 * and a menu library would ride on all of them for one dropdown. Escape and a
 * click outside close it, and focus goes back to the avatar.
 */
export function AccountMenu({ locale, user }: { locale: string; user: AuthUser }) {
  const t = useTranslations("nav");
  const tCommon = useTranslations("common");
  const router = useRouter();
  const pathname = usePathname();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function signOut() {
    setSigningOut(true);
    setOpen(false);
    try {
      await logout();
      router.replace(`/${locale}`);
    } finally {
      setSigningOut(false);
    }
  }

  const base = `/${locale}`;
  const close = () => setOpen(false);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={t("accountMenu")}
        aria-expanded={open}
        aria-controls={panelId}
        disabled={signingOut}
        onClick={() => setOpen((value) => !value)}
        className="flex rounded-full outline-none ring-2 ring-white ring-offset-2 ring-offset-primary/15 transition-opacity duration-150 ease-out hover:opacity-90 focus-visible:ring-primary disabled:opacity-50"
      >
        <AccountAvatar user={user} className="h-9 w-9 text-sm" />
      </button>

      {open ? (
        <div
          id={panelId}
          className="absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border border-neutral-200 bg-white p-1 shadow-card"
        >
          <div className="px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-neutral-900">{user.name}</p>
            <p className="truncate text-xs text-neutral-500">{user.email}</p>
            {user.isInternal ? (
              <p className="mt-1.5 inline-flex rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600" title={tCommon("internalAccessHint")}>
                {tCommon("internalAccessLabel")}
              </p>
            ) : null}
          </div>
          <div className="my-1 h-px bg-neutral-200" aria-hidden />
          <IntentLink href={`${base}/dashboard`} onClick={close} className={itemClass}>
            <LayoutDashboard className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
            {t("dashboard")}
          </IntentLink>
          <IntentLink href={`${base}/dashboard/library`} onClick={close} className={itemClass}>
            <BookOpen className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
            {t("myCourses")}
          </IntentLink>
          <IntentLink href={`${base}/u/${encodeURIComponent(toPublicProfileSlug(user))}`} onClick={close} className={itemClass}>
            <User className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
            {t("profile")}
          </IntentLink>
          {/* `SUP-WEB01`: the person's tickets; the page they are on goes with a new one. */}
          <IntentLink href={supportHomeHref(locale, pathname ?? undefined)} onClick={close} className={itemClass}>
            <LifeBuoy className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
            {t("support")}
          </IntentLink>
          <div className="my-1 h-px bg-neutral-200" aria-hidden />
          <button type="button" onClick={() => void signOut()} className={cn(itemClass, "text-red-700 hover:bg-red-50 hover:text-red-700")}>
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            {t("logout")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
